import {
  Box,
  Grid,
  Theme,
  useTheme,
  Accordion,
  IconButton,
  Typography,
  AccordionDetails,
  AccordionSummary,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import type {
  EventType,
  BucketsType,
  EventPackage,
} from "@/global/store/data/types";
import { MarqueeText } from "@/shared/common/layouts/MarqueeText/MarqueeText";
import { CombinedEventCard } from "@/shared/common/EventCard/CombinedEventCard";
import { isBucketMinMaxEventSelectionReached } from "../utils.events";

type BucketsWrapperProps = {
  selectedEvents: EventType[];
  availableBuckets: BucketsType[];
  selectedPackage: EventPackage | null;
  handleOpenBucket(bucketId: number): void;
  onClickPreview?: (eventSelected: EventType) => void;
  handleSelect: (event: EventType, bucketId: number) => void;
  handleDeselectEvent: (event: EventType, bucketId: number) => void;

  shouldApplyMargin?: boolean;
};

export const BucketsWrapper: FC<BucketsWrapperProps> = ({
  // states
  selectedEvents,
  selectedPackage,
  availableBuckets,
  shouldApplyMargin,

  // handlers
  handleSelect,
  onClickPreview,
  handleOpenBucket,
  handleDeselectEvent,
}) => {
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");
  const styles = useStyles({ theme });
  const showMarquee = useIsMobile();

  const teamConfig = useTeamConfig();

  return (
    <Box>
      <Box mt={shouldApplyMargin ? 4 : 0}>
        {availableBuckets.map((bucket, idx) => {
          return (
            <Box
              key={bucket.id}
              sx={{
                mb: idx < availableBuckets.length - 1 ? 4 : 0,
              }}
            >
              <Accordion
                sx={styles.accordion}
                expanded={bucket.opened}
                disabled={bucket.disabled}
                onChange={(e) => {
                  if (bucket.disableAccordionExpand) return e.stopPropagation();
                  handleOpenBucket(bucket.id as number);
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <IconButton>
                      <MdOutlineKeyboardArrowDown />
                    </IconButton>
                  }
                  sx={{ ".MuiAccordionSummary-content": { margin: 0 } }}
                >
                  <Box width={"100%"} textAlign={"left"}>
                    <Typography
                      variant="h6"
                      fontWeight={"700"}
                      color="text.primary"
                    >
                      {bucket.title}
                    </Typography>

                    {bucket.description ? (
                      <Box>
                        <Box
                          borderRadius={2}
                          width={"100%"}
                          height={"24px"}
                          pr={1}
                        >
                          {showMarquee ? (
                            <MarqueeText
                              text={bucket.description}
                              minLength={16}
                              style={{
                                fontSize: "12px",
                                fontWeight: 500,
                              }}
                            />
                          ) : (
                            <Typography fontSize={"12px"} fontWeight={"500"}>
                              {bucket.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ) : null}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={styles.accordionDetails}>
                  <Box mt={1} width="100%">
                    <Grid container spacing={4}>
                      {bucket.events.map((evt, idx) => {
                        const isSelected = selectedEvents.some((ev) => {
                          return ev.eventCode == evt.eventCode;
                        });
                        const { isMaxEventCountReached } =
                          isBucketMinMaxEventSelectionReached(
                            bucket,
                            selectedEvents,
                            teamConfig
                          );

                        return (
                          <Grid key={idx} item xs={12} md={6} lg={4} xl={3}>
                            <CombinedEventCard
                              variant={"upcoming"}
                              event={evt}
                              isDisabled={isMaxEventCountReached && !isSelected}
                              isSelected={isSelected}
                              onClickPreview={() => onClickPreview?.(evt)}
                              onSelect={() => {
                                if (isSelected)
                                  return handleDeselectEvent(
                                    evt,
                                    bucket.id as number
                                  );
                                handleSelect(evt, bucket.id as number);
                              }}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 700);

  useEffect(() => {
    const onResize = () => {
      setIsMobile(window.innerWidth <= 700);
    };
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return isMobile;
}

const useStyles = ({ theme }: { theme: Theme }) => ({
  accordion: {
    paddingTop: 1,
    paddingBottom: 1,
    boxShadow: "none",
    borderRadius: "6 !important",
    border: `1px solid ${theme.palette.divider}`,

    "&.MuiAccordionSummary-root": {
      paddingLeft: 2,
      paddingRight: 2,
    },

    "& .MuiAccordionSummary-expandIconWrapper svg": {
      width: "1.5rem",
      height: "1.5rem",
    },
  },
  accordionDetails: {
    borderTop: `1px solid ${theme.palette.divider}`,
  },
});
